import {Component, EventEmitter, inject, OnInit, Output, signal} from '@angular/core';
import {CommonModule, DatePipe, NgOptimizedImage} from '@angular/common';
import {FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {UserService} from '@services/user.service';
import {ApiService} from '@services/api.service';
import {UtilsService} from '@services/utils.service';
import {ActivatedRoute, RouterLink} from '@angular/router';
import {map, tap} from 'rxjs/operators';
import {Base64Model, IntegrateTypeInput, SimpleMessage} from '../../models/input.model';
import {ImsDocumentDetaiLDownload, Request, RequestIntegrateResponse, RequestMessage} from '../../models/output.model';
import {Observable} from 'rxjs';
import {ModalService} from '@services/modal.service';

@Component({
  selector: 'app-request',
  imports: [
    DatePipe, CommonModule, ReactiveFormsModule, NgOptimizedImage, RouterLink
  ],
  templateUrl: './request.component.html',
  styleUrl: './request.component.css'
})
export class RequestComponent implements OnInit {

  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute)

  item$!: Observable<Request> ;
  @Output() goBackToMyRequests = new EventEmitter<void>();


  selectedFiles: File[] = [];
  base64Files: Base64Model[] = [];

  result: string = '';
  userName: string = '';

  activeTab = signal('messages')
  submitted = signal(false);
  selectedSender = signal<string | null>(null);

  constructor(
    private userService: UserService,
    private utilsSvc: UtilsService,
    private apiService: ApiService,
    private modalService: ModalService,
    ) {
  }

  messagesForm = new Map<number, FormGroup>();

  goBack() {
    this.goBackToMyRequests.emit()
  }

  sendIntegration(messageId: number) {
    if (this.getFormForMessage(messageId).valid) {

      const formData: IntegrateTypeInput = {
        ...this.getFormForMessage(messageId).value,
        attachments: this.base64Files
      }

      this.apiService.integrateRequest(formData).subscribe({
        next: (response: RequestIntegrateResponse) => {
          this.submitted.set(true);
          this.result = response.result.data.uuid ? "Successo" : "Errore";
          const body = {
            statusText: response.result.data?.uuid ? "Operazione eseguita con Successo" : (response.result?.message || "Errore"),
            message: response.result.data?.uuid ? "Richiesta inviata Correttamente" : "...qualcosa è andato storto"
          }
          this.modalService.showMessage(body)
          setTimeout(()=> {
            this.modalService.closeMessage()
          },5000)
          const id = this.route.snapshot.paramMap.get('id') || "";

          this.item$ = this.apiService.getRequest(id).pipe(
            map((res) => res.data ),
            map((resp) => {
              resp.messages.sort((a,b) => new Date(a.date).getTime() - new Date(b.date).getTime() );
              return resp
            }),
            tap(() => {
              this.submitted.set(false)
          }))
        },
        error: (error) => {
          this.result = error.error.message
        },
        complete: () => {
          this.fetchRequest()
        }
      });

    }
  }

  onFileSelected(event: Event) {
    this.selectedFiles = [];
    this.base64Files = [];
    this.utilsSvc.onFileSelected(event, this.selectedFiles, this.base64Files )
  }

  selectTab(tabName: string) {
    this.activeTab.set(tabName);
  }

  ngOnInit(): void {
    this.userName = this.userService.getUser()?.name || '';
    this.fetchRequest()
  }

  getFormForMessage(messageId: number): FormGroup {
    const form = this.messagesForm.get(messageId);
    if (!form) {
      console.error(`Form not found for message ${messageId}`);
    }
    return form || new FormGroup({});
  }

  fetchRequest() {
    const id = this.route.snapshot.paramMap.get('id') || "";
    this.item$ = this.apiService.getRequest(id).pipe(
      map((res) => res.data ),
      map((resp) => {
        resp.messages.sort((a,b) => new Date(a.date).getTime() - new Date(b.date).getTime() );
        return resp
      }),
      tap((res  ) => {
        this.buildForms(res)
      })
    )
  }

  buildForms(item: Request) {
    this.messagesForm.clear();
      item.messages.forEach((message: RequestMessage) => {
        this.messagesForm.set(message.id, this.fb.group({
          hr_request_uuid: new FormControl(this.route.snapshot.paramMap.get('id'), [Validators.required]),
          activity_id: new FormControl(message.activity_id, []),
          parent_message_id: new FormControl(message.id, [Validators.required]),
          message: new FormControl('', [Validators.required]),
          mail: new FormControl(this.userService.getUser()?.username, [Validators.required, Validators.email]),
          attachments: new FormControl<Base64Model[]>([])
        }))
      })
  }

  getStateColor(stato: string) {
    return {
      'bg-[#80a6da] text-white': stato === 'in_progress' || stato === 'ready_to_close' ,//|| stato === 'taking_charge',
      'bg-green-100 text-green-700 border border-green-200': stato === 'closed', //ok
      'bg-red-100 text-red-700 border border-red-200': stato === 'rejected', //ok
      'bg-white': stato === 'to_do' || stato === 'draft',
      'bg-white border-[1px] border-blue-100': stato === 'to_do' || stato === 'draft' || stato === 'taking_charge',
      'bg-yellow-100 border border-yellow-200': stato === 'integration_required'
    };
  }

  getInitials(name: string): string {
    if (!name) return '';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
  }

  selectSender(sender: string) {
    this.selectedSender.set(sender);
  }

  getThreadMessages(allMessages: RequestMessage[], sender: string): RequestMessage[] {
    const threadMessages: RequestMessage[] = [];
    const senderLower = sender.toLowerCase();

    // Identifichiamo i messaggi "root" del mittente selezionato
    const roots = allMessages.filter(m => m.from.toLowerCase() === senderLower && !m.message_parent_id);

    // Se non ci sono root diretti, prendiamo tutti i messaggi del mittente
    if (roots.length === 0) {
       return allMessages.filter(m => m.from.toLowerCase() === senderLower ||
          (m.message_parent_id && allMessages.find(p => p.id === m.message_parent_id)?.from.toLowerCase() === senderLower))
          .sort((a,b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    }

    // Altrimenti ricostruiamo i thread partendo dai root
    const rootIds = new Set(roots.map(r => r.id));

    return allMessages.filter(m =>
      rootIds.has(m.id) ||
      (m.message_parent_id && rootIds.has(m.message_parent_id)) ||
      m.from.toLowerCase() === senderLower // Includiamo comunque i messaggi del mittente
    ).sort((a,b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }

  getSenders(messages: RequestMessage[]): string[] {
    const senders = new Set<string>();
    messages.forEach(m => {
      // In stile WhatsApp, i thread sono raggruppati per l'interlocutore.
      // Se il messaggio non è dell'utente corrente, allora è un interlocutore (mittente del thread).
      if (m.from.toLowerCase() !== this.userName.toLowerCase()) {
        senders.add(m.from);
      }
    });
    const result = Array.from(senders);
    // Se non c'è un mittente selezionato e ci sono mittenti, selezioniamo il primo per default
    // Usiamo un timeout per evitare l'errore ExpressionChangedAfterItHasBeenCheckedError se chiamato durante il render
    if (!this.selectedSender() && result.length > 0) {
      setTimeout(() => {
        if (!this.selectedSender()) {
          this.selectedSender.set(result[0]);
        }
      });
    }
    return result;
  }

  downloadDocument(id: number, type: string) {
    this.apiService.getDocument(id).subscribe((response: ImsDocumentDetaiLDownload) => {
      this.utilsSvc.transformBlobToLink(response, type);
    });
  }
}


